// src/pages/ResignationSettlement.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, MenuItem, Button, Chip,
  Divider, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Tooltip, IconButton, Alert
} from '@mui/material';
import { Refresh as RefreshIcon, Print as PrintIcon, CheckCircle as CheckIcon } from '@mui/icons-material';

const STAFF_API    = "https://ab.pizzahub.lk/api/get_staff.php";
const SETTLE_API   = "https://ab.pizzahub.lk/api/resignation_settlement.php";

// format helper
const fmt = (n, d=2) => Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: d, maximumFractionDigits: d
});

export default function ResignationSettlement() {
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [from, setFrom] = useState(() => {
    const dt = new Date(); dt.setDate(1);
    return dt.toISOString().slice(0,10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0,10));
  const [dayDivisor, setDayDivisor] = useState(30);
  const [tipsAwarded, setTipsAwarded] = useState('');
  const [svcAwarded, setSvcAwarded]   = useState('');
  const [settleLoanNow, setSettleLoanNow] = useState(false);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizedId, setFinalizedId] = useState(null);

  const loadStaff = () => {
    fetch(STAFF_API)
      .then(r=>r.json())
      .then(d => setStaff(Array.isArray(d) ? d : (d.items||[])))
      .catch(()=>setStaff([]));
  };

  useEffect(() => { loadStaff(); }, []);

  const selected = useMemo(() => {
    const id = parseInt(staffId,10);
    return staff.find(s => Number(s.id) === id);
  }, [staffId, staff]);

  const calc = async () => {
    if (!staffId) return alert("Select staff");
    if (!from || !to) return alert("Select date range");
    setLoading(true);
    setFinalizedId(null);
    try {
      const q = new URLSearchParams({
        staff_id: String(staffId),
        from, to,
        day_divisor: String(dayDivisor),
        settle_loan: settleLoanNow ? '1' : '0',
      });
      if (tipsAwarded !== '') q.append('tips_awarded', String(tipsAwarded));
      if (svcAwarded  !== '') q.append('service_awarded', String(svcAwarded));
      const res = await fetch(`${SETTLE_API}?${q.toString()}`).then(r=>r.json());
      if (res.status !== 'ok') { alert(res.message || 'Failed to compute'); return; }
      setSummary(res.data);
    } catch {
      alert("Network error");
    } finally {
      setLoading(false);
    }
  };

  const finalizeSettlement = async () => {
    if (!summary) return;
    if (!window.confirm("Finalize settlement and save a record?")) return;
    setFinalizing(true);
    try {
      const payload = {
        staff_id: summary.staff.id,
        from: summary.period.from,
        to: summary.period.to,
        day_divisor: summary.day_divisor,
        settle_loan: summary.flags.settle_loan ? 1 : 0,
        tips_awarded: summary.earnings.tips_awarded,
        service_awarded: summary.earnings.service_awarded,
        note: ''
      };
      const res = await fetch(SETTLE_API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(payload)
      }).then(r=>r.json());
      if (res.status !== 'ok') { alert(res.message || 'Failed to finalize'); return; }
      setFinalizedId(res.id);
      alert(`Settlement saved (ID #${res.id}).`);
    } catch {
      alert("Network error");
    } finally {
      setFinalizing(false);
    }
  };

  const earnTotal = summary ? (
    Number(summary.earnings.basic_earned||0)
    + Number(summary.earnings.tips_awarded||0)
    + Number(summary.earnings.service_awarded||0)
    + Number(summary.adjustments.plus_total||0)
  ) : 0;

  const dedTotal = summary ? (
    Number(summary.deductions.advances_total||0)
    + Number(summary.adjustments.minus_total||0)
    + Number(summary.deductions.loan_deduction||0)
  ) : 0;

  const netPayable = earnTotal - dedTotal;

  return (
    <Box sx={{ p:2 }}>
      <Typography variant="h4" gutterBottom>Resignation Salary Settlement</Typography>

      <Paper sx={{ p:2, mb:2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField select label="Staff" size="small" value={staffId} onChange={e=>setStaffId(e.target.value)} sx={{ minWidth:260 }}>
            {staff.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name} {s.phone ? `· ${s.phone}`:''}</MenuItem>
            ))}
          </TextField>
          <TextField label="From" type="date" size="small" InputLabelProps={{ shrink:true }} value={from} onChange={e=>setFrom(e.target.value)}/>
          <TextField label="To (Last working day)" type="date" size="small" InputLabelProps={{ shrink:true }} value={to} onChange={e=>setTo(e.target.value)}/>
          <TextField select size="small" label="Day divisor" value={dayDivisor} onChange={e=>setDayDivisor(Number(e.target.value))}>
            <MenuItem value={30}>30 (calendar-month)</MenuItem>
            <MenuItem value={26}>26 (work-days)</MenuItem>
            <MenuItem value={1}>Custom (enter below)</MenuItem>
          </TextField>
          {dayDivisor === 1 && (
            <TextField size="small" type="number" label="Custom Divisor" value={dayDivisor} onChange={e=>setDayDivisor(Number(e.target.value)||1)} />
          )}
          <TextField size="small" type="number" label="Tip Award (override)" value={tipsAwarded} onChange={e=>setTipsAwarded(e.target.value)} />
          <TextField size="small" type="number" label="Service Charge Award (override)" value={svcAwarded} onChange={e=>setSvcAwarded(e.target.value)} />
          <TextField select size="small" label="Settle Loan Now?" value={settleLoanNow ? 'yes' : 'no'} onChange={e=>setSettleLoanNow(e.target.value==='yes')}>
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes (deduct outstanding)</MenuItem>
          </TextField>
          <Box sx={{ flexGrow:1 }} />
          <Button variant="contained" startIcon={<RefreshIcon/>} onClick={calc}>Calculate</Button>
        </Stack>
      </Paper>

      {!summary && !loading && (
        <Alert severity="info">Select staff and date range, then click <strong>Calculate</strong>.</Alert>
      )}

      {loading && <CircularProgress size={22} />}

      {summary && (
        <>
          {/* Staff + Period */}
          <Paper sx={{ p:2, mb:2 }}>
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">{summary.staff.name}</Typography>
              <Chip label={`Basic Salary: Rs ${fmt(summary.staff.basic_salary)}`} color="default" />
              <Chip label={`Day Rate: Rs ${fmt(summary.day_rate)}`} color="default" />
              <Chip label={`Period: ${summary.period.from} → ${summary.period.to}`} />
              <Chip label={`Eligible Days: ${fmt(summary.attendance.eligible_days,2)} (Full ${summary.attendance.full_days} · Half ${summary.attendance.half_days})`} />
              <Chip label={`Tips-eligible days: ${fmt(summary.attendance.tip_eligible_days,2)}`} />
              <Chip label={`Service-eligible days: ${fmt(summary.attendance.service_eligible_days,2)}`} />
            </Stack>
          </Paper>

          {/* Totals Row */}
          <Paper sx={{ p:2, mb:2 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Box>
                <Typography variant="subtitle2">Earnings</Typography>
                <Typography variant="h6" sx={{ color:'green' }}>Rs {fmt(earnTotal)}</Typography>
                <Typography variant="body2">
                  Basic {fmt(summary.earnings.basic_earned)} + Tips {fmt(summary.earnings.tips_awarded)} + Service {fmt(summary.earnings.service_awarded)} + Adj+ {fmt(summary.adjustments.plus_total)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Deductions</Typography>
                <Typography variant="h6" sx={{ color:'crimson' }}>Rs {fmt(dedTotal)}</Typography>
                <Typography variant="body2">
                  Advances {fmt(summary.deductions.advances_total)} + Adj− {fmt(summary.adjustments.minus_total)} + Loan {fmt(summary.deductions.loan_deduction)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Net Payable</Typography>
                <Typography variant="h5" sx={{ color: netPayable >= 0 ? 'green' : 'crimson' }}>
                  Rs {fmt(netPayable)}
                </Typography>
              </Box>

              {/* <Box sx={{ ml:'auto', display:'flex', gap:1 }}>
                <Button variant="outlined" startIcon={<PrintIcon/>} onClick={()=>window.print()}>Print</Button>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon/>}
                  color="primary"
                  onClick={finalizeSettlement}
                  disabled={finalizing}
                >
                  {finalizing ? 'Saving…' : 'Finalize Settlement'}
                </Button>
              </Box> */}
            </Stack>
            {finalizedId && (
              <Alert severity="success" sx={{ mt:2 }}>
                Settlement saved with ID <strong>#{finalizedId}</strong>.
              </Alert>
            )}
          </Paper>

          {/* Breakdown: Earnings */}
          <Paper sx={{ p:2, mb:2 }}>
            <Typography variant="h6" gutterBottom>Earnings Breakdown</Typography>
            <Divider sx={{ mb:2 }}/>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Chip label={`Basic Earned: Rs ${fmt(summary.earnings.basic_earned)}`} color="success" />
              <Chip label={`Tips Awarded: Rs ${fmt(summary.earnings.tips_awarded)}`} />
              <Chip label={`Service Awarded: Rs ${fmt(summary.earnings.service_awarded)}`} />
              <Chip label={`Adjustments (+): Rs ${fmt(summary.adjustments.plus_total)}`} />
            </Stack>
            <Table size="small" sx={{ mt:2 }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={4}><strong>Positive Adjustments</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ID</TableCell><TableCell>Year-Month</TableCell><TableCell>Type</TableCell><TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.adjustments.items || []).filter(a => a.sign === '+').map(a => (
                  <TableRow key={`ap${a.id}`}>
                    <TableCell>{a.id}</TableCell>
                    <TableCell>{a.year}-{String(a.month).padStart(2,'0')}</TableCell>
                    <TableCell>{a.type}</TableCell>
                    <TableCell align="right">{fmt(a.amount)}</TableCell>
                  </TableRow>
                ))}
                {!(summary.adjustments.items||[]).some(a=>a.sign==='+') && (
                  <TableRow><TableCell colSpan={4} align="center">No positive adjustments</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Breakdown: Deductions */}
          <Paper sx={{ p:2, mb:2 }}>
            <Typography variant="h6" gutterBottom>Deductions Breakdown</Typography>
            <Divider sx={{ mb:2 }}/>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Chip label={`Advances: Rs ${fmt(summary.deductions.advances_total)}`} color="error" />
              <Chip label={`Adjustments (−): Rs ${fmt(summary.adjustments.minus_total)}`} color="error" />
              <Chip label={`Loan Deduction: Rs ${fmt(summary.deductions.loan_deduction)}`} color="error" />
            </Stack>

            <Typography variant="subtitle1" sx={{ mt:2 }}>Loans</Typography>
            <Table size="small" sx={{ mb:2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Monthly Installment</TableCell>
                  <TableCell>Balance Installments</TableCell>
                  <TableCell>Outstanding (calc)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.loans.length ? summary.loans.map(l => (
                  <TableRow key={l.id}>
                    <TableCell>{l.id}</TableCell>
                    <TableCell>Rs {fmt(l.monthly_installment)}</TableCell>
                    <TableCell>{l.balance_installments}</TableCell>
                    <TableCell>Rs {fmt(l.outstanding_amount)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={4} align="center">No loan records</TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            <Typography variant="subtitle1">Advance Salary (in range)</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.advances.length ? summary.advances.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{a.id}</TableCell>
                    <TableCell>{a.advance_date}</TableCell>
                    <TableCell align="right">Rs {fmt(a.amount)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={3} align="center">No advances in range</TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            <Typography variant="subtitle1" sx={{ mt:2 }}>Negative Adjustments</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell><TableCell>Year-Month</TableCell><TableCell>Type</TableCell><TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.adjustments.items || []).filter(a => a.sign === '-').map(a => (
                  <TableRow key={`am${a.id}`}>
                    <TableCell>{a.id}</TableCell>
                    <TableCell>{a.year}-{String(a.month).padStart(2,'0')}</TableCell>
                    <TableCell>{a.type}</TableCell>
                    <TableCell align="right">{fmt(a.amount)}</TableCell>
                  </TableRow>
                ))}
                {!(summary.adjustments.items||[]).some(a=>a.sign==='-') && (
                  <TableRow><TableCell colSpan={4} align="center">No negative adjustments</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Attendance details */}
          <Paper sx={{ p:2, mb:4 }}>
            <Typography variant="h6" gutterBottom>Attendance (Summary)</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={`Full Days: ${summary.attendance.full_days}`} color="success" />
              <Chip label={`Half Days: ${summary.attendance.half_days}`} color="warning" />
              <Chip label={`Eligible: ${fmt(summary.attendance.eligible_days,2)}`} />
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
}
